import { useMutation, useQuery } from '@tanstack/react-query';
import {
  type BankAccountResponse,
  formatMoney,
  fromCents,
  INSTALMENT_BODIES,
  INSTALMENT_BODY_LABELS,
  INSTALMENT_FREQUENCIES,
  INSTALMENT_METHOD_LABELS,
  INSTALMENT_METHODS,
  type InstalmentBody,
  type InstalmentFrequency,
  type InstalmentMethod,
  type InstalmentPlanRequest,
  type InstalmentPlanResponse,
  instalmentSchedule,
  toCents,
} from '@k-management/shared';
import { useState } from 'react';
import { ConfirmDialog } from '../../components/ConfirmDialog.js';
import { DecimalInput } from '../../components/DecimalInput.js';
import { Field } from '../../components/Field.js';
import { apiRequest } from '../../lib/api.js';
import { moneyOf } from '../people/people-ui.js';
import { SubjectPicker } from './SubjectPayments.js';

interface Line {
  id?: string;
  dueDate: string;
  /** As typed. */
  amount: string;
  interest: string;
  paid: boolean;
}

/**
 * A rateazione created or changed (3.15): the body, the subject, how it is
 * paid and its instalments, made by themselves from the total or written one
 * by one; the paid ones keep their amount.
 */
export function InstalmentPlanForm({
  plan,
  onClose,
  onSaved,
}: {
  plan: InstalmentPlanResponse | null;
  onClose: () => void;
  onSaved: (plan: InstalmentPlanResponse) => void;
}) {
  const banks = useQuery({
    queryKey: ['treasury', 'banks'],
    queryFn: () => apiRequest<BankAccountResponse[]>('/treasury/banks'),
  });
  const [subject, setSubject] = useState<{ id: string; name: string } | null>(
    plan ? { id: plan.subjectId, name: plan.subjectName } : null,
  );
  const [body, setBody] = useState<InstalmentBody>(plan?.body ?? 'entrate');
  const [name, setName] = useState(plan?.name ?? '');
  const [reference, setReference] = useState(plan?.reference ?? '');
  const [method, setMethod] = useState<InstalmentMethod>(plan?.method ?? 'f24');
  const [accountCode, setAccountCode] = useState(plan?.accountCode ?? '');
  const [maxMissed, setMaxMissed] = useState(plan?.maxMissed ? String(plan.maxMissed) : '');
  const [notes, setNotes] = useState(plan?.notes ?? '');
  const [lines, setLines] = useState<Line[]>(
    (plan?.dues ?? []).map((due) => ({
      id: due.id,
      dueDate: due.dueDate,
      amount: due.amount,
      interest: due.interest,
      paid: due.state === 'pagata',
    })),
  );
  // The plan made by itself.
  const [total, setTotal] = useState('');
  const [count, setCount] = useState('');
  const [firstDue, setFirstDue] = useState('');
  const [frequency, setFrequency] = useState<InstalmentFrequency>('trimestrale');

  const generate = () => {
    const amount = moneyOf(total);
    if (!amount || !firstDue || !Number(count)) return;
    const paid = lines.filter((line) => line.paid);
    setLines([
      ...paid,
      ...instalmentSchedule(amount, Number(count), firstDue, INSTALMENT_FREQUENCIES[frequency]).map(
        (line) => ({
          dueDate: line.dueDate,
          amount: line.amount,
          interest: line.interest,
          paid: false,
        }),
      ),
    ]);
  };
  const parsed = lines.map((line) => ({
    ...line,
    amountValue: moneyOf(line.amount),
    interestValue: moneyOf(line.interest || '0'),
  }));
  const valid =
    subject &&
    name.trim() &&
    parsed.length > 0 &&
    parsed.every(
      (line) =>
        line.dueDate &&
        line.amountValue &&
        toCents(line.amountValue) > 0 &&
        line.interestValue !== null,
    );
  const sorted = [...parsed].sort((a, b) => a.dueDate.localeCompare(b.dueDate));
  const save = useMutation({
    mutationFn: () => {
      const request: InstalmentPlanRequest = {
        subjectId: subject!.id,
        body,
        name: name.trim(),
        reference: reference.trim() || null,
        method,
        accountCode: accountCode || null,
        maxMissed: Number(maxMissed) || null,
        notes: notes.trim() || null,
        dues: sorted.map((line, index) => ({
          ...(line.id ? { id: line.id } : {}),
          number: index + 1,
          dueDate: line.dueDate,
          amount: line.amountValue!,
          interest: line.interestValue!,
        })),
      };
      return plan
        ? apiRequest<InstalmentPlanResponse>(`/treasury/instalment-plans/${plan.id}`, {
            method: 'PUT',
            body: request,
          })
        : apiRequest<InstalmentPlanResponse>('/treasury/instalment-plans', {
            method: 'POST',
            body: request,
          });
    },
    onSuccess: onSaved,
  });
  const sum = fromCents(
    parsed.reduce((cents, line) => cents + (line.amountValue ? toCents(line.amountValue) : 0), 0),
  );
  const update = (index: number, change: Partial<Line>) =>
    setLines(lines.map((line, at) => (at === index ? { ...line, ...change } : line)));

  return (
    <ConfirmDialog
      title={plan ? `Rateazione · ${plan.name}` : 'Nuova rateazione'}
      confirmLabel="Salva"
      busy={save.isPending}
      disabled={!valid}
      error={save.error?.message}
      onConfirm={() => save.mutate()}
      onCancel={onClose}
      wide
    >
      <div className="form-grid">
        <Field label="Ente">
          <select value={body} onChange={(event) => setBody(event.target.value as InstalmentBody)}>
            {INSTALMENT_BODIES.map((item) => (
              <option key={item} value={item}>
                {INSTALMENT_BODY_LABELS[item]}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Soggetto in Anagrafica">
          <SubjectPicker value={subject} onChange={setSubject} />
        </Field>
        <Field label="Nome">
          <input
            value={name}
            maxLength={120}
            placeholder="Es. Avviso bonario 2023"
            onChange={(event) => setName(event.target.value)}
          />
        </Field>
        <Field label="Riferimento" hint="Avviso, cartella, protocollo.">
          <input
            value={reference}
            maxLength={100}
            onChange={(event) => setReference(event.target.value)}
          />
        </Field>
        <Field label="Come si paga">
          <select
            value={method}
            onChange={(event) => setMethod(event.target.value as InstalmentMethod)}
          >
            {INSTALMENT_METHODS.map((item) => (
              <option key={item} value={item}>
                {INSTALMENT_METHOD_LABELS[item]}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Conto">
          <select value={accountCode} onChange={(event) => setAccountCode(event.target.value)}>
            <option value="">—</option>
            {(banks.data ?? [])
              .filter((account) => account.kind === 'Banca')
              .map((account) => (
                <option key={account.code} value={account.code}>
                  {account.alias}
                </option>
              ))}
          </select>
        </Field>
        <Field
          label="Avvisa dopo rate scadute"
          hint="Quante rate non pagate fanno rischiare la decadenza; vuoto: nessun avviso."
        >
          <input
            type="number"
            min={1}
            max={99}
            value={maxMissed}
            onChange={(event) => setMaxMissed(event.target.value)}
          />
        </Field>
      </div>
      <Field label="Note">
        <textarea rows={2} value={notes} onChange={(event) => setNotes(event.target.value)} />
      </Field>

      <h3>Rate</h3>
      <div className="row">
        <Field label="Totale da rateizzare">
          <DecimalInput value={total} onChange={setTotal} decimals={[2, 2]} />
        </Field>
        <Field label="Numero di rate">
          <input
            type="number"
            min={1}
            max={240}
            value={count}
            onChange={(event) => setCount(event.target.value)}
          />
        </Field>
        <Field label="Prima rata">
          <input
            type="date"
            value={firstDue}
            onChange={(event) => setFirstDue(event.target.value)}
          />
        </Field>
        <Field label="Ogni">
          <select
            value={frequency}
            onChange={(event) => setFrequency(event.target.value as InstalmentFrequency)}
          >
            {Object.keys(INSTALMENT_FREQUENCIES).map((item) => (
              <option key={item} value={item}>
                {item}
              </option>
            ))}
          </select>
        </Field>
        <button type="button" onClick={generate}>
          Genera le rate
        </button>
      </div>
      <p className="muted small">
        Le rate generate prendono il posto di quelle non pagate; poi si correggono una per una (es.
        gli interessi scritti nell’avviso).
      </p>
      <table className="data-table">
        <thead>
          <tr>
            <th>#</th>
            <th>Scadenza</th>
            <th className="numeric">Rata</th>
            <th className="numeric">di cui interessi</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {lines.map((line, index) => (
            <tr key={line.id ?? `new-${index}`}>
              <td>{sorted.findIndex((item) => item === parsed[index]) + 1}</td>
              <td>
                <input
                  type="date"
                  value={line.dueDate}
                  onChange={(event) => update(index, { dueDate: event.target.value })}
                />
              </td>
              <td style={{ width: 140 }}>
                {line.paid ? (
                  formatMoney(line.amount)
                ) : (
                  <DecimalInput
                    value={line.amount}
                    onChange={(value) => update(index, { amount: value })}
                    decimals={[2, 2]}
                  />
                )}
              </td>
              <td style={{ width: 140 }}>
                <DecimalInput
                  value={line.interest}
                  onChange={(value) => update(index, { interest: value })}
                  decimals={[2, 2]}
                />
              </td>
              <td>
                {line.paid ? (
                  <span className="pill ok">pagata</span>
                ) : (
                  <button
                    type="button"
                    className="link small"
                    onClick={() => setLines(lines.filter((_, at) => at !== index))}
                  >
                    Togli
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={2}>
              <button
                type="button"
                className="link"
                onClick={() =>
                  setLines([...lines, { dueDate: '', amount: '', interest: '0.00', paid: false }])
                }
              >
                Aggiungi una rata
              </button>
            </td>
            <td className="numeric">
              <strong>{formatMoney(sum)}</strong>
            </td>
            <td colSpan={2} />
          </tr>
        </tfoot>
      </table>
    </ConfirmDialog>
  );
}
